import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { BUILD_VERSION } from 'config';
import { mutedSelector, blockNotificationsSelector } from 'features/identity/preferences';
import greenAlarmSound from 'assets/audio/greenAlarm.mp3';
import { useLocale } from 'lib';
import { TranslationKey } from 'config/i18n';

const POLL_INTERVAL_MS = 15 * 60 * 1000;
const INITIAL_DELAY_MS = 5000;
const NOTIFICATION_INTERVAL_MS = 7500;
const MAX_NOTIFICATIONS = 5;

async function fetchDeployedVersion(): Promise<string | null> {

  const url = `/version.json?t=${Date.now()}`;
  const res = await fetch(url);
  if (!res.ok) return null;
  const data = await res.json();
  return typeof data?.version === 'string' ? data.version : null;

}

function playUpdateSoundLoop(src: string, signal: AbortSignal): void {

  const audio = new Audio(src);
  signal.addEventListener('abort', () => audio.pause());
  if (typeof audio.loop !== 'undefined') {

    audio.loop = true;
    audio.play().catch(() => {});
    return;

  }
  const phase = (): void => {

    audio.currentTime = 0;
    audio.play().then(() => {

      audio.addEventListener('ended', () => {

        if (!signal.aborted) phase();

      }, { once: true });

    }).catch(() => {});

  };
  phase();

}

function startVersionNotifications(
  text: string,
  onCleanup: (cleanup: () => void) => void,
): void {

  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  const notifications: Notification[] = [];
  const show = () => {

    try {

      const n = new Notification(text);
      n.onclick = () => window.focus();
      notifications.push(n);
      if (notifications.length > MAX_NOTIFICATIONS) {

        notifications.shift()?.close();

      }

    } catch {
      // ignore
    }

  };
  const id = setInterval(show, NOTIFICATION_INTERVAL_MS);
  show();
  onCleanup(() => {

    clearInterval(id);
    notifications.forEach((n) => n.close?.());

  });

}

export function useVersionCheck(): boolean {

  const [newVersionAvailable, setNewVersionAvailable] = useState(false);
  const { t } = useLocale();
  const muted = useSelector(mutedSelector);
  const blockNotifications = useSelector(blockNotificationsSelector);
  const notificationCleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {

    let cancelled = false;
    const currentBuild = BUILD_VERSION;

    const check = async () => {

      const deployed = await fetchDeployedVersion();
      if (cancelled || deployed === null) return;
      if (deployed !== currentBuild) {

        setNewVersionAvailable(true);

      }

    };

    const initialTimer = setTimeout(check, INITIAL_DELAY_MS);
    const intervalId = setInterval(check, POLL_INTERVAL_MS);

    return () => {

      cancelled = true;
      clearTimeout(initialTimer);
      clearInterval(intervalId);
      notificationCleanupRef.current?.();
      notificationCleanupRef.current = null;

    };

  }, []);

  useEffect(() => {

    if (!newVersionAvailable || muted || blockNotifications) return undefined;
    const controller = new AbortController();
    playUpdateSoundLoop(greenAlarmSound, controller.signal);
    return () => controller.abort();

  }, [newVersionAvailable, muted, blockNotifications]);

  useEffect(() => {

    if (!newVersionAvailable) return undefined;
    const key: TranslationKey = 'Notification.Version_Available';
    startVersionNotifications(t(key), (cleanup) => {

      notificationCleanupRef.current = cleanup;

    });
    return () => {

      notificationCleanupRef.current?.();
      notificationCleanupRef.current = null;

    };

  }, [newVersionAvailable, t]);

  return newVersionAvailable;

}
